import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const UserPreferencesPage = () => {
  const [preferences, setPreferences] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Collapsible sections state
  const [openSections, setOpenSections] = useState({
    profile: true,
    notifications: false,
    security: false
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Modal state
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prefsResponse, profileResponse] = await Promise.all([
        axios.get(`${API}/admin/broadcast-preferences`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      setPreferences(prefsResponse.data);
      setUserProfile(profileResponse.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: err.response?.data?.detail || 'Failed to load user data'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/admin/broadcast-preferences`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/broadcast-preferences`,
        { channel_preferences: preferences.channel_preferences },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSuccessModal({
        isOpen: true,
        title: 'Preferences Saved',
        message: 'Your notification preferences have been updated successfully.'
      });
      await fetchPreferences();
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setErrorModal({
        isOpen: true,
        title: 'Save Failed',
        message: err.response?.data?.detail || 'Failed to save preferences'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (category, channel) => {
    setPreferences(prev => ({
      ...prev,
      channel_preferences: {
        ...prev.channel_preferences,
        [channel]: {
          ...prev.channel_preferences[channel],
          [category]: !prev.channel_preferences[channel][category]
        }
      }
    }));
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please fill in all password fields'
      });
      return;
    }

    // Strong password validation (matching backend requirements)
    const password = passwordData.newPassword;
    if (password.length < 8) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Password must be at least 8 characters long'
      });
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Password must contain at least one uppercase letter'
      });
      return;
    }
    if (!/[a-z]/.test(password)) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Password must contain at least one lowercase letter'
      });
      return;
    }
    if (!/[0-9]/.test(password)) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Password must contain at least one number'
      });
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'New passwords do not match'
      });
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/admin/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setSuccessModal({
        isOpen: true,
        title: 'Password Changed',
        message: 'Your password has been changed successfully.'
      });
      
      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      console.error('Failed to change password:', err);
      setErrorModal({
        isOpen: true,
        title: 'Password Change Failed',
        message: err.response?.data?.detail || 'Failed to change password'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  // Collapsible Section Component
  const CollapsibleSection = ({ sectionId, title, tagline, icon, children }) => {
    const isOpen = openSections[sectionId];
    const headerRef = React.useRef(null);

    const handleHeaderClick = (e) => {
      if (headerRef.current && headerRef.current.contains(e.target)) {
        setOpenSections(prev => ({
          ...prev,
          [sectionId]: !prev[sectionId]
        }));
      }
    };

    return (
      <div className="border-b border-slate-700 last:border-b-0">
        <div
          ref={headerRef}
          onClick={handleHeaderClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpenSections(prev => ({
                ...prev,
                [sectionId]: !prev[sectionId]
              }));
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          className="w-full bg-purple-700 hover:bg-purple-600 text-white transition-colors duration-150 cursor-pointer"
        >
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {icon && (
                <span className="text-xl">{icon}</span>
              )}
              <div className="text-left">
                <h2 className="text-lg font-semibold">{title}</h2>
                {tagline && (
                  <p className="text-purple-200 text-sm mt-0.5">{tagline}</p>
                )}
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-white flex-shrink-0 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {isOpen && (
          <div className="px-6 py-6 bg-slate-800">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-gray-400">Loading preferences...</span>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8 text-center">
        <p className="text-gray-400">Failed to load preferences</p>
        <button onClick={fetchData} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500">Retry</button>
      </div>
    );
  }

  // Helper to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">User Settings</h1>
        <p className="text-purple-200">Manage your profile, notifications and account security settings</p>
      </div>

      {/* Collapsible Sections Container */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        
        {/* User Profile Section */}
        <CollapsibleSection
          sectionId="profile"
          title="User Profile"
          tagline="Your account information"
          icon="👤"
        >
          {userProfile ? (
            <div className="space-y-4">
              {/* Profile Header with Photo */}
              <div className="flex items-start gap-4 pb-4 border-b border-slate-700">
                <div className="flex-shrink-0">
                  {userProfile.profile_photo_url ? (
                    <img
                      src={userProfile.profile_photo_url}
                      alt={userProfile.full_name}
                      className="w-20 h-20 rounded-full object-cover border-2 border-purple-500"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
                      {userProfile.first_name?.charAt(0)}{userProfile.last_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">{userProfile.full_name}</h3>
                  <p className="text-sm text-gray-400 capitalize">{userProfile.role} User</p>
                </div>
              </div>

              {/* Profile Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Account Information */}
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-700/50">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <span className="text-purple-400">🔑</span> Account Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Username</span>
                      <span className="font-medium text-white">{userProfile.username}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Account Created</span>
                      <span className="font-medium text-white">{formatDate(userProfile.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Last Login</span>
                      <span className="font-medium text-white">{formatDateTime(userProfile.last_login_timestamp)}</span>
                    </div>
                  </div>
                </div>

                {/* Organisation Information */}
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-700/50">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <span className="text-purple-400">🏢</span> Organisation
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Organisation Name</span>
                      <span className="font-medium text-white">{userProfile.organisation_name || 'Not assigned'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Organisation ID</span>
                      <span className="font-mono text-xs text-gray-300 bg-slate-600 px-2 py-0.5 rounded">{userProfile.organisation_id || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-700/50">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <span className="text-purple-400">📧</span> Contact Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Email</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{userProfile.email || 'Not set'}</span>
                        {userProfile.email && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            userProfile.email_verified 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {userProfile.email_verified ? '✓ Verified' : '⚠ Not Verified'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Phone</span>
                      <span className="font-medium text-white">{userProfile.phone_number || 'Not set'}</span>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="border border-slate-700 rounded-lg p-4 bg-slate-700/50">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <span className="text-purple-400">📋</span> Personal Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Date of Birth</span>
                      <span className="font-medium text-white">{formatDate(userProfile.date_of_birth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Address</span>
                      <span className="font-medium text-white text-right max-w-[200px] truncate" title={userProfile.address}>
                        {userProfile.address || 'Not set'}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Info Note */}
              <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3 mt-2">
                <p className="text-xs text-purple-300">
                  <strong>Note:</strong> To update your profile information, please contact SiteOrbit Support.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400">
              <p>Unable to load profile information</p>
            </div>
          )}
        </CollapsibleSection>
        
        {/* Account Security Section */}
        <CollapsibleSection
          sectionId="security"
          title="Account Security"
          tagline="Manage your password and security settings"
          icon="🔐"
        >
          <div className="space-y-6">
            {/* Change Password Form */}
            <div className="border border-slate-700 rounded-lg p-4 bg-slate-700/50">
              <h3 className="font-semibold text-white mb-4">Change Password</h3>
              
              <div className="space-y-4 max-w-md">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm pr-10 bg-slate-700 text-white placeholder-gray-400"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                    >
                      {showCurrentPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm pr-10 bg-slate-700 text-white placeholder-gray-400"
                      placeholder="Enter new password (min. 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                    >
                      {showNewPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm pr-10 bg-slate-700 text-white placeholder-gray-400"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <p className="mt-1 text-sm text-red-400">Passwords do not match</p>
                  )}
                </div>

                {/* Password Requirements Info */}
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-sm text-amber-300">
                    <strong>Password Requirements:</strong>
                  </p>
                  <ul className="text-xs text-amber-200 mt-1 space-y-0.5">
                    <li>• Minimum 8 characters</li>
                    <li>• At least one uppercase letter (A-Z)</li>
                    <li>• At least one lowercase letter (a-z)</li>
                    <li>• At least one number (0-9)</li>
                    <li>• At least one special character (!@#$%^&* etc.)</li>
                  </ul>
                </div>

                {/* Change Password Button */}
                <div className="pt-2">
                  <button
                    onClick={handlePasswordChange}
                    disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword || passwordData.newPassword !== passwordData.confirmPassword}
                    className="px-4 py-2 bg-purple-600 text-white hover:bg-purple-500 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Notification Preferences Section */}
        <CollapsibleSection
          sectionId="notifications"
          title="Notification Preferences"
          tagline="Choose which notifications you'd like to receive"
          icon="🔔"
        >
          <div className="space-y-3">
            {/* Notification Grid - Compact Layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              
              {/* Essential Notifications */}
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <span>🔴</span>
                  <h3 className="font-semibold text-white text-sm">Essential</h3>
                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">Required</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">Security & system alerts</p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <input type="checkbox" checked disabled className="w-3 h-3 rounded bg-slate-600" />
                    📧 Email
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <input type="checkbox" checked disabled className="w-3 h-3 rounded bg-slate-600" />
                    💬 SMS
                  </label>
                </div>
              </div>

              {/* Marketing Notifications */}
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <span>🔵</span>
                  <h3 className="font-semibold text-white text-sm">Marketing</h3>
                </div>
                <p className="text-xs text-gray-400 mb-2">Offers & promotions</p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-purple-400">
                    <input
                      type="checkbox"
                      checked={preferences?.channel_preferences?.email?.marketing || false}
                      onChange={() => handleToggle('marketing', 'email')}
                      className="w-3 h-3 text-purple-500 rounded focus:ring-purple-500 bg-slate-600 border-slate-500"
                    />
                    📧 Email
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-purple-400">
                    <input
                      type="checkbox"
                      checked={preferences?.channel_preferences?.sms?.marketing || false}
                      onChange={() => handleToggle('marketing', 'sms')}
                      className="w-3 h-3 text-purple-500 rounded focus:ring-purple-500 bg-slate-600 border-slate-500"
                    />
                    💬 SMS
                  </label>
                </div>
              </div>

              {/* Platform Updates */}
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <span>🟣</span>
                  <h3 className="font-semibold text-white text-sm">Updates</h3>
                </div>
                <p className="text-xs text-gray-400 mb-2">Features & improvements</p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-purple-400">
                    <input
                      type="checkbox"
                      checked={preferences?.channel_preferences?.email?.platform_updates || false}
                      onChange={() => handleToggle('platform_updates', 'email')}
                      className="w-3 h-3 text-purple-500 rounded focus:ring-purple-500 bg-slate-600 border-slate-500"
                    />
                    📧 Email
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-purple-400">
                    <input
                      type="checkbox"
                      checked={preferences?.channel_preferences?.sms?.platform_updates || false}
                      onChange={() => handleToggle('platform_updates', 'sms')}
                      className="w-3 h-3 text-purple-500 rounded focus:ring-purple-500 bg-slate-600 border-slate-500"
                    />
                    💬 SMS
                  </label>
                </div>
              </div>

            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 bg-purple-600 text-white hover:bg-purple-500 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </CollapsibleSection>

      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
        title={successModal.title}
        message={successModal.message}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default UserPreferencesPage;
